import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, DatePicker, Button, Switch } from 'antd';
import { toast } from 'react-toastify';
import { BreathAPIs } from '../../APIs/BreathAPIs';
import styles from './styles.module.css';
import moment from 'moment';

const EditCouponForm = ({ modalVisible, closeModal, refreshCoupons, selectedCoupon }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const [locationsData, setLocationsData] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [isForPublicLocation, setIsForPublicLocation] = useState(false); // New state for public location

    // Fetch locations when modal opens
    useEffect(() => {
        if (modalVisible) {
            fetchLocations();
            loadCouponData();
        }
    }, [modalVisible]);

    // Fetch available locations
    const fetchLocations = async () => {
        setLoadingLocations(true);
        try {
            const response = await BreathAPIs.getDashboardFilterLocations();
            setLocationsData(response.data.locations || []);
        } catch (error) {
            toast.error("Error fetching locations");
        }
        setLoadingLocations(false);
    };

    // Load existing coupon details into form
    const loadCouponData = () => {
        if (selectedCoupon) {
            setIsForPublicLocation(selectedCoupon.isForPublicLocation || false);

            form.setFieldsValue({
                couponName: selectedCoupon.couponName,
                locations: selectedCoupon.locations?.map(loc => loc._id) || [], // Multiple locations
                benefit: selectedCoupon.benefit,
                type: selectedCoupon.type,
                numberOfUsage: selectedCoupon.numberOfUsage,
                expiry: selectedCoupon.expiry ? moment(selectedCoupon.expiry) : null,
                enabled: selectedCoupon.enabled,
            });
        }
    };

    const handleCancel = () => {
        closeModal();
        form.resetFields();
        setIsForPublicLocation(false); // Reset public location toggle
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {
            const payload = {
                ...values,
                isForPublicLocation, // Include public location field
                locations: isForPublicLocation ? [] : values.locations, // Clear locations if public
            };

            const response = await BreathAPIs.editCoupon(selectedCoupon._id, payload);
            toast.success(response.data.message);
            refreshCoupons();
            handleCancel();
        } catch (error) {
            toast.error(error.response?.data?.message || "Error updating coupon");
        }
        setIsLoading(false);
    };

    return (
        <Modal
            title="Edit Coupon"
            open={modalVisible}
            onCancel={handleCancel}
            footer={null}
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                    label="Coupon Name"
                    name="couponName"
                    rules={[{ required: true, message: "Coupon name is required" }]}
                >
                    <Input placeholder="Enter coupon name" />
                </Form.Item>

                <Form.Item label="Public Location" name="isForPublicLocation">
                    <Switch
                        checked={isForPublicLocation}
                        onChange={setIsForPublicLocation}
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                    />
                </Form.Item>

                <Form.Item
                    label="Locations"
                    name="locations"
                    // rules={[{ required: !isForPublicLocation, message: "Please select at least one location" }]}
                >
                    <Select
                        mode="multiple"
                        showSearch
                        placeholder={isForPublicLocation ? "Disabled for Public Location" : "Select Locations"}
                        style={{ width: '100%' }}
                        loading={loadingLocations}
                        disabled={isForPublicLocation} // Disable selection if public location is checked
                        options={locationsData.map(element => ({
                            value: element._id,
                            label: element.locationName
                        }))}
                        filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                        }
                        allowClear
                    />
                </Form.Item>

                <Form.Item
                    label="Benefit Amount ($)"
                    name="benefit"
                    rules={[{ required: true, message: "Please enter the benefit amount in dollars." }]}
                >
                    <InputNumber min={0} max={20} style={{ width: '100%' }} placeholder="Enter benefit amount in $" />
                </Form.Item>

                <Form.Item
                    label="Coupon Type"
                    name="type"
                    rules={[{ required: true, message: "Please select a type" }]}
                >
                    <Select placeholder="Select type">
                        <Select.Option value="account-creation">Account Creation</Select.Option>
                        <Select.Option value="promotion">Promotion</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item label="Number of Usage" name="numberOfUsage">
                    <InputNumber allowClear min={0} max={100000} style={{ width: '100%' }} placeholder="Enter number of usage" />
                </Form.Item>

                <Form.Item label="Expiry Date" name="expiry">
                    <DatePicker
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Select expiry date"
                        format="YYYY-MM-DD"
                        disabledDate={(current) => {
                            return current && current < moment().startOf('day') && current.format("YYYY-MM-DD") !== form.getFieldValue('expiry')?.format("YYYY-MM-DD");
                        }}
                    />
                </Form.Item>

                <Form.Item label="Enabled" name="enabled" valuePropName="checked">
                    <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </Form.Item>

                <Button type="primary" htmlType="submit" loading={isLoading} style={{ width: '100%' }}>
                    {isLoading ? "Saving..." : "Update Coupon"}
                </Button>
            </Form>
        </Modal>
    );
};

export default EditCouponForm;
